/**
 * Unified material factory for all viewport object types.
 *
 * Every renderer (SceneRenderer, MannequinRenderer, PrimitiveRenderer) calls
 * {@link createViewportMaterial} instead of building materials inline. This
 * ensures consistent visual output across object types and eliminates
 * duplicated material creation logic.
 *
 * Every call allocates a fresh material. The caller owns the returned
 * instance and is responsible for disposing it (typically via a useEffect
 * cleanup keyed on the useMemo result). Three.js / WebGPU's internal
 * pipeline cache dedupes compiled shader programs for structurally
 * identical materials, so JS-level sharing is unnecessary.
 */

import { uniform } from "three/tsl";
import * as THREE from "three/webgpu";
import type { ViewMode } from "../app/store.ts";
import { useStore } from "../app/store.ts";
import type { SceneMaterial } from "../modeling/types.ts";
import type { DebugMaterialConfig } from "./debugMaterial.ts";
import {
  createCheckerboardTexture,
  createEmissionGlowTexture,
  createMetallicGradientTexture,
  createProceduralNormalMap,
  createRoughnessCheckerboardTexture,
} from "./debugMaterial.ts";
import {
  buildPatternBumpNormalNode,
  buildPatternOverlayColorNode,
  type GridLines,
  type GridPattern,
} from "./gridOverlayNode.ts";
import { MATCAP_PRESETS } from "./matcapPresets.ts";
import { SURFACE_XRAY_OPACITY } from "./sharedMaterials.ts";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Resolved texture objects ready for assignment to a Three.js material. */
export interface TextureSlots {
  baseColor?: THREE.Texture; // sRGB color space
  normal?: THREE.Texture; // linear color space
  roughness?: THREE.Texture; // linear color space
  metallic?: THREE.Texture; // linear color space
  emission?: THREE.Texture; // sRGB color space
}

/** Surface properties for one material slot. */
export interface MaterialConfig {
  color: THREE.ColorRepresentation;
  roughness: number;
  metalness: number;
  emissive?: THREE.ColorRepresentation;
  emissiveIntensity?: number;
  flatShading: boolean;
  textures?: TextureSlots;

  // Surface
  normalIntensity?: number;
  opacity?: number;
  specularIntensity?: number;

  // Coating
  clearcoat?: number;
  clearcoatRoughness?: number;

  // Sheen
  sheen?: number;
  sheenRoughness?: number;
  sheenColor?: THREE.ColorRepresentation;

  // Glass / Transmission
  transmission?: number;
  ior?: number;
  thickness?: number;

  // World-space surface pattern overlay
  gridPattern?: GridPattern;
  gridCellSize?: number;
  gridColor?: string;
  gridOpacity?: number;
  gridLines?: GridLines;
  gridGroutThickness?: number;
  gridBumpScale?: number;
  gridRotation?: 0 | 45 | 90;
  gridProjection?: "tangent" | "triplanar";
}

/** Viewport-level rendering options that apply uniformly to all objects. */
export interface ViewportMaterialOptions {
  viewMode: ViewMode;
  matcapPreset: string;
  objectXray: boolean;
  wireframeXray: boolean;
  showTextures: boolean;
  textureMapsEnabled: boolean;
}

function applySurfaceXray<
  T extends THREE.Material & {
    opacity: number;
    transparent: boolean;
    depthWrite: boolean;
  },
>(material: T, enabled: boolean): T {
  if (!enabled) return material;
  material.transparent = true;
  material.opacity = Math.min(material.opacity, SURFACE_XRAY_OPACITY);
  material.depthWrite = false;
  return material;
}

// ---------------------------------------------------------------------------
// SceneMaterial → MaterialConfig helper
// ---------------------------------------------------------------------------

/**
 * Convert a SceneMaterial to a MaterialConfig, centralizing the mapping so
 * individual renderers don't repeat it.
 */
export function sceneMaterialToConfig(
  mat: SceneMaterial,
  flatShading: boolean,
  textures?: TextureSlots,
  /**
   * Fallback grid projection used when the material has no explicit
   * `gridProjection` set. Callers typically derive this from the object
   * kind (see `getDefaultGridProjection`) so curved primitives like
   * spheres and cylinders render their grids in triplanar mode by default
   * without forcing every material to carry an explicit value.
   */
  defaultGridProjection?: "tangent" | "triplanar",
): MaterialConfig {
  return {
    color: mat.color,
    roughness: mat.roughness,
    metalness: mat.metalness,
    emissive: mat.emissiveIntensity ? mat.emissiveColor : undefined,
    emissiveIntensity: mat.emissiveIntensity,
    normalIntensity: mat.normalIntensity,
    opacity: mat.opacity,
    specularIntensity: mat.specularIntensity,
    clearcoat: mat.clearcoat,
    clearcoatRoughness: mat.clearcoatRoughness,
    sheen: mat.sheen,
    sheenRoughness: mat.sheenRoughness,
    sheenColor: mat.sheenColor,
    transmission: mat.transmission,
    ior: mat.ior,
    thickness: mat.thickness,
    gridPattern: mat.gridPattern,
    gridCellSize: mat.gridCellSize,
    gridColor: mat.gridColor,
    gridOpacity: mat.gridOpacity,
    gridLines: mat.gridLines,
    gridGroutThickness: mat.gridGroutThickness,
    gridBumpScale: mat.gridBumpScale,
    gridRotation: mat.gridRotation,
    gridProjection: mat.gridProjection ?? defaultGridProjection,
    flatShading,
    textures,
  };
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/** Build a MaterialConfig + options from the debug material state. */
function applyDebugOverride(
  dbg: DebugMaterialConfig,
  options: ViewportMaterialOptions,
): { config: MaterialConfig; options: ViewportMaterialOptions } {
  const textures: TextureSlots = {};
  if (dbg.albedoMapEnabled)
    textures.baseColor = createCheckerboardTexture(dbg.albedoMapScale);
  if (dbg.normalMapEnabled) textures.normal = createProceduralNormalMap();
  if (dbg.roughnessMapEnabled)
    textures.roughness = createRoughnessCheckerboardTexture(
      dbg.roughnessMapScale,
    );
  if (dbg.metallicMapEnabled)
    textures.metallic = createMetallicGradientTexture();
  if (dbg.emissionMapEnabled) textures.emission = createEmissionGlowTexture();

  const hasAnyTex = Object.keys(textures).length > 0;

  return {
    config: {
      color: dbg.color,
      roughness: dbg.roughness,
      metalness: dbg.metalness,
      emissive: dbg.emissiveIntensity > 0 ? dbg.emissiveColor : undefined,
      emissiveIntensity: dbg.emissiveIntensity,
      normalIntensity: dbg.normalMapIntensity,
      opacity: dbg.opacity,
      specularIntensity: dbg.specularIntensity,
      clearcoat: dbg.clearcoat,
      clearcoatRoughness: dbg.clearcoatRoughness,
      sheen: dbg.sheen,
      sheenRoughness: dbg.sheenRoughness,
      sheenColor: dbg.sheenColor,
      transmission: dbg.transmission,
      ior: dbg.ior,
      thickness: dbg.thickness,
      flatShading: dbg.flatShading,
      textures: hasAnyTex ? textures : undefined,
    },
    options: {
      ...options,
      showTextures: hasAnyTex,
      textureMapsEnabled: hasAnyTex,
    },
  };
}

/**
 * Allocate a fresh viewport material for the given config.
 *
 * The returned material is owned by the caller and must be disposed when
 * it is replaced or its owning component unmounts. When the debug material
 * override is active, the incoming config is replaced with procedural
 * textures and uniform scalar values for system validation.
 */
export function createViewportMaterial(
  config: MaterialConfig,
  options: ViewportMaterialOptions,
): THREE.Material {
  // Debug material override — replaces per-object material config globally
  const dbg = useStore.getState().debugMaterial;
  if (dbg) {
    const overridden = applyDebugOverride(dbg, options);
    config = overridden.config;
    options = overridden.options;
  }

  // Render-mode global pattern override — forces per-material gridPattern on or
  // off while the workspace is in Render mode. "defer" keeps the
  // per-material setting unchanged and is the default.
  const { workspaceMode, renderGridOverride } = useStore.getState();
  if (workspaceMode === "render" && renderGridOverride !== "defer") {
    if (renderGridOverride === "off") {
      config = { ...config, gridPattern: undefined };
    } else if (renderGridOverride === "on" && !config.gridPattern) {
      // Force-enable a uniform grid using any existing cell/color/opacity settings.
      config = { ...config, gridPattern: "uniform" };
    }
  }

  const {
    viewMode,
    matcapPreset,
    objectXray,
    wireframeXray,
    showTextures,
    textureMapsEnabled,
  } = options;
  const { color, roughness, metalness, flatShading, textures } = config;

  if (viewMode === "wireframe") {
    return new THREE.MeshBasicMaterial({
      color,
      wireframe: true,
      side: THREE.DoubleSide,
      depthTest: !wireframeXray,
    });
  }

  if (viewMode === "matcap") {
    const ns = config.normalIntensity ?? 1;
    const preset = MATCAP_PRESETS[matcapPreset];
    const mat = new THREE.MeshMatcapMaterial({
      color,
      matcap: preset?.create(),
      flatShading,
      side: THREE.DoubleSide,
    });
    if (showTextures && textures) {
      if (textures.baseColor) mat.map = textures.baseColor;
      if (textureMapsEnabled && textures.normal) {
        mat.normalMap = textures.normal;

        if (ns !== 1) mat.normalScale = new THREE.Vector2(ns, ns);
      }
    }
    return applySurfaceXray(mat, objectXray);
  }

  // material — MeshPhysicalMaterial with full PBR
  const ns = config.normalIntensity ?? 1;
  const mat = new THREE.MeshPhysicalMaterial({
    color,
    roughness,
    metalness,
    flatShading,
    side: THREE.DoubleSide,
    envMapIntensity: 1.0,
  });

  if (showTextures && textures) {
    if (textures.baseColor) mat.map = textures.baseColor;

    if (textureMapsEnabled) {
      if (textures.normal) {
        mat.normalMap = textures.normal;

        if (ns !== 1) mat.normalScale = new THREE.Vector2(ns, ns);
      }
      // Scalar roughness/metalness multiplies the map value (correct PBR behavior)
      if (textures.roughness) mat.roughnessMap = textures.roughness;
      if (textures.metallic) mat.metalnessMap = textures.metallic;
      if (textures.emission) mat.emissiveMap = textures.emission;
    }
  }

  // Emissive
  if (config.emissive != null) {
    mat.emissive = new THREE.Color(config.emissive);
    mat.emissiveIntensity = config.emissiveIntensity ?? 0;
  }

  // Opacity / transparency
  const opacity = config.opacity ?? 1;
  if (opacity < 1) {
    mat.transparent = true;
    mat.opacity = opacity;
  }

  // Specular
  if (config.specularIntensity !== undefined)
    mat.specularIntensity = config.specularIntensity;

  // Coating
  if (config.clearcoat) mat.clearcoat = config.clearcoat;
  if (config.clearcoatRoughness)
    mat.clearcoatRoughness = config.clearcoatRoughness;

  // Sheen
  if (config.sheen) {
    mat.sheen = config.sheen;
    if (config.sheenRoughness !== undefined)
      mat.sheenRoughness = config.sheenRoughness;
    if (config.sheenColor) mat.sheenColor = new THREE.Color(config.sheenColor);
  }

  // Transmission (glass)
  if (config.transmission) {
    mat.transmission = config.transmission;
    mat.transparent = true;
    if (config.ior !== undefined) mat.ior = config.ior;
    if (config.thickness !== undefined) mat.thickness = config.thickness;
  }

  // World-space surface pattern overlay — UV-independent perspective aid.
  // Replaces the base-color node with a pattern blend; the rest of the PBR
  // pipeline (roughness, metalness, lighting) still applies.
  if (config.gridPattern) {
    const pattern = config.gridPattern;
    const projection = config.gridProjection ?? "tangent";
    // Default colors differ by pattern: uniform uses white, grout-based patterns use gray.
    const defaultColor = pattern === "uniform" ? "#ffffff" : "#888888";
    const defaultCellSize =
      pattern === "brick" ? 0.075 : pattern === "tile" ? 0.3 : 1;

    const baseColorNode = uniform(
      new THREE.Color(config.color as THREE.ColorRepresentation),
    );
    const cellSizeNode = uniform(config.gridCellSize ?? defaultCellSize);
    const colorNode = uniform(
      new THREE.Color(config.gridColor ?? defaultColor),
    );
    const opacityNode = uniform(config.gridOpacity ?? 0.6);
    const groutFracNode = uniform(config.gridGroutThickness ?? 0.06);
    const lines: GridLines = config.gridLines ?? "both";
    const rotationRad = ((config.gridRotation ?? 0) * Math.PI) / 180;

    const patternColorNode = buildPatternOverlayColorNode(
      pattern,
      projection,
      baseColorNode,
      cellSizeNode,
      colorNode,
      opacityNode,
      groutFracNode,
      lines,
      rotationRad,
    );

    (mat as unknown as { colorNode: unknown }).colorNode = patternColorNode;

    // The material cache key uses '{}' for any object-typed property (including
    // colorNode / normalNode), so two materials with the same grid pattern
    // structure produce the same cache key and share a NodeBuilderState. That
    // state's cloned bindings reference the FIRST compiled material's
    // UniformNode instances, so every subsequent material renders with the
    // first material's uniform values. Setting customProgramCacheKey to the
    // colorNode's UUID (unique per buildPatternOverlayColorNode call) gives
    // each material its own NodeBuilderState and correctly isolated bindings.
    (
      mat as unknown as { customProgramCacheKey: () => string }
    ).customProgramCacheKey = () => patternColorNode.uuid;

    // Screen-space normal perturbation — simulates 3D grout relief on
    // brick and tile. Only applied when the user opts in (bumpScale > 0).
    const bumpScale = config.gridBumpScale ?? 0;
    if (bumpScale > 0 && (pattern === "brick" || pattern === "tile")) {
      (mat as unknown as { normalNode: unknown }).normalNode =
        buildPatternBumpNormalNode(
          pattern,
          projection,
          cellSizeNode,
          groutFracNode,
          uniform(bumpScale),
          rotationRad,
        );
    }
  }

  return applySurfaceXray(mat, objectXray);
}
